'use client'

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { revalidateLogic, useStore } from '@tanstack/react-form'
import { addDays } from 'date-fns'
import { Building2, Lock } from 'lucide-react'
import {
  HiMiniChevronLeft,
  HiMiniChevronRight,
  HiOutlineArrowTopRightOnSquare,
} from 'react-icons/hi2'
import { toast } from 'sonner'
import { twMerge } from 'tailwind-merge'

import { useAuthInfo } from '@sweetauth/core'

import Button from '@/components/common/Button'
import Chip from '@/components/common/Chip'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/common/Dialog'
import { SharePointIcon } from '@/components/common/LogoIcon'
import { Separator } from '@/components/common/Separator'
import Spinner from '@/components/common/Spinner'
import { SearchInput } from '@/components/common/TextInputV2'
import type { WizardOption } from '@/components/common/Wizard/OptionsStep'
import { OptionsStep } from '@/components/common/Wizard/OptionsStep'
import { WizardStepper } from '@/components/common/Wizard/Stepper'
import FileUpload, { CompactLocalFile } from '@/components/files/FileUpload'
import ErrorMessage from '@/components/form/ErrorMessage'
import Label from '@/components/form/Label'
import type { AnyTanstackAppForm } from '@/components/form/tanstackFormTypes'
import {
  type SharepointFile,
  SharepointFilePicker,
  type SharepointFilePickerSelection,
} from '@/components/integrations/SharepointFilePicker'
import { MembersStep } from '@/components/pursuits/v3/dialogs/CreatePursuitDialog/MembersStep'
import { WhoGetsAccess } from '@/components/pursuits/v3/dialogs/CreatePursuitDialog/WhoGetsAccess'
import {
  DynamicFieldList,
  OverviewSection,
  buildDefaultValuesFromPursuit,
  buildZodObjectFromExtendedSchema,
  useAppForm,
} from '@/components/pursuits/v3/dialogs/EditPursuitDialog'

import { cleanFileName } from '@/helpers/file'
import { includeCurrentUserForInviteOnly } from '@/helpers/permissions'

import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useLatestPursuitSchema } from '@/hooks/pursuits/schemas/usePursuitSchemas'
import { useCreatePursuit } from '@/hooks/pursuits/useCreatePursuit'
import { usePursuitStages } from '@/hooks/pursuits/usePursuitStages'
import { useUploadPursuitFilesPresignedWithoutPursuitId } from '@/hooks/pursuits/useUploadPursuitFilesPresigned'
import { useImportSharepointFiles } from '@/hooks/sharepoint/useImportSharepointFiles'
import { useSharepointDropdownOption } from '@/hooks/sharepoint/useSharepointDropdownOption'

import type { JsonValue } from '@/types/extended-schema'
import type { UUID } from '@/types/Id'
import type {
  CreatePursuit,
  Pursuit,
  PursuitType,
} from '@/types/pursuit/Pursuit'
import type { PursuitStage } from '@/types/pursuit/Stage'

type WizardStep = 'visibility' | 'members' | 'details'

interface WizardStepDef {
  id: WizardStep
  label: string
  heading: string
  description: string
}

const VISIBILITY_STEP: WizardStepDef = {
  id: 'visibility',
  label: 'Visibility',
  heading: 'Who can access this pursuit?',
  description:
    "You can change this anytime from the pursuit's access settings.",
}

const MEMBERS_STEP: WizardStepDef = {
  id: 'members',
  label: 'Members',
  heading: 'Invite teammates',
  description:
    "Invited teammates get full access and appear as owners. You're included automatically, and you can update members anytime.",
}

const DETAILS_STEP: WizardStepDef = {
  id: 'details',
  label: 'Details',
  heading: 'Pursuit details',
  description:
    'Upload solicitation files and fill in the essentials — everything can be updated later.',
}

const PUBLIC_WIZARD_STEPS: WizardStepDef[] = [VISIBILITY_STEP, DETAILS_STEP]

const PRIVATE_WIZARD_STEPS: WizardStepDef[] = [
  VISIBILITY_STEP,
  MEMBERS_STEP,
  DETAILS_STEP,
]

const VISIBILITY_OPTIONS: WizardOption[] = [
  {
    id: 'public',
    label: 'Team pursuit',
    description:
      'Everyone in your organization can access and collaborate on this pursuit.',
    icon: Building2,
    tileClassName:
      'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  },
  {
    id: 'private',
    label: 'Private pursuit',
    description:
      'An isolated workspace for you and invited teammates. Only members and admins can access it.',
    icon: Lock,
    tileClassName:
      'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
    // Private is the amber concept everywhere else in the app, so its
    // selected state goes amber instead of accent.
    selectedClassName:
      'border-amber-400 dark:border-amber-500/60 bg-amber-50/70 dark:bg-amber-500/10 shadow-sm ring-1 ring-amber-400/25 dark:ring-amber-500/20',
    selectedIndicatorClassName: 'bg-amber-500',
  },
]

interface CreatePursuitDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess?: (pursuit: Pursuit) => void
}

export interface CreatePursuitFormValues {
  title: string
  description: string
  stage_id: UUID | ''
  user_ids: string[]
  due_date: Date
  type: PursuitType
  tag_ids: UUID[]
  estimated_value: number | null
  pwin: number
  has_unrestricted_access: boolean
  values: Record<string, JsonValue>
}

// Hook for form logic
interface UseCreatePursuitFormProps {
  orgId: UUID | ''
  onClose: () => void
  onSuccess?: (pursuit: Pursuit) => void
}

interface PendingSharepointFile extends SharepointFile {
  tag_ids: UUID[]
}

const getStageIndex = (stage: PursuitStage): number => stage.index
const getStageByIndex = (
  stages: PursuitStage[],
  index: number,
): PursuitStage | undefined => stages.find((stage) => stage.index === index)

const selectMinStage = (stages: PursuitStage[]): PursuitStage | undefined => {
  if (stages.length === 0) return undefined
  const indices = stages.map(getStageIndex)
  const minIndex = Math.min(...indices)
  return getStageByIndex(stages, minIndex)
}

const mergeSharepointFiles = (
  currentFiles: PendingSharepointFile[],
  nextFiles: PendingSharepointFile[],
) => {
  const filesById = new Map(
    currentFiles.map((file) => [
      `${file.drive_id}:${file.drive_item_id}`,
      file,
    ]),
  )

  for (const file of nextFiles) {
    filesById.set(`${file.drive_id}:${file.drive_item_id}`, file)
  }

  return Array.from(filesById.values())
}

interface CompactSharepointFileProps {
  file: PendingSharepointFile
  className?: string
  onDelete?: () => void
}

const CompactSharepointFile: React.FC<CompactSharepointFileProps> = ({
  file,
  className,
  onDelete,
}) => {
  return (
    <Chip
      className={twMerge(
        'flex items-center gap-2 p-[1px] px-2 max-w-full rounded-full',
        className,
      )}
      onDelete={onDelete}
    >
      <div className="flex items-center gap-[6px] ml-1 truncate">
        <SharePointIcon size={14} />
        <span className="text-[13px] truncate">
          {cleanFileName(file.name ?? 'SharePoint file')}
        </span>
        {file.tag_ids.length > 0 ? (
          <span className="text-[11px] text-primary-500 dark:text-primary-400">
            {file.tag_ids.length} tag{file.tag_ids.length === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
    </Chip>
  )
}

function useCreatePursuitForm({
  orgId,
  onClose,
  onSuccess,
}: UseCreatePursuitFormProps) {
  const { user } = useAuthInfo()
  const { data: schema } = useLatestPursuitSchema({ orgId })
  const { data: minStage } = usePursuitStages({
    orgId,
    selector: selectMinStage,
  })
  const { mutateAsync: createPursuit, isPending: isCreatingPursuit } =
    useCreatePursuit({
      orgId,
    })
  const {
    mutateAsync: uploadPursuitFiles,
    isPending: isUploadingPursuitFiles,
  } = useUploadPursuitFilesPresignedWithoutPursuitId()
  const {
    mutateAsync: importSharepointFiles,
    isPending: isImportingSharepointFiles,
  } = useImportSharepointFiles({ orgId })

  // Retry mode ("pursuit created, uploads pending") lives in BOTH a ref and
  // state: the state is the render input (refs don't re-render, so the footer
  // hint/buttons would otherwise only update when some unrelated state
  // happens to flip), while the ref gives post-await closures a fresh read.
  // Always write through markPursuitSaved so the two can't diverge.
  const savedPursuitRef = useRef<Pursuit | null>(null)
  const [savedPursuit, setSavedPursuit] = useState<Pursuit | null>(null)
  const markPursuitSaved = useCallback((pursuit: Pursuit | null) => {
    savedPursuitRef.current = pursuit
    setSavedPursuit(pursuit)
  }, [])
  // Bumped whenever the dialog closes. Submit handlers capture the value at
  // entry and abort all post-await side effects if it changed — otherwise a
  // Cancel/Escape during an in-flight create or retry-upload lets the still-
  // running closure re-populate state the close already reset (poisoning the
  // next wizard session) or navigate a user who explicitly cancelled.
  const submitGenerationRef = useRef(0)
  const [selectedFiles, setSelectedFiles] = useState<File[]>([])
  const [selectedSharepointFiles, setSelectedSharepointFiles] = useState<
    PendingSharepointFile[]
  >([])
  const [isSharepointPickerOpen, setIsSharepointPickerOpen] =
    useState<boolean>(false)
  const [fieldSearchQuery, setFieldSearchQuery] = useState<string>('')

  const handleOpenSharepointPicker = useCallback(() => {
    setIsSharepointPickerOpen(true)
  }, [])

  const {
    handleSelectSharepoint,
    showSharepoint,
    sharepointDisabled,
    sharepointRequiresOrgSetup,
    sharepointSublabel,
    sharepointSublabelTone,
  } = useSharepointDropdownOption({
    orgId,
    onAuthenticated: handleOpenSharepointPicker,
  })

  const isUploadingFiles = isUploadingPursuitFiles || isImportingSharepointFiles

  const defaultValues: CreatePursuitFormValues = useMemo(
    () => ({
      title: '',
      description: '',
      stage_id: minStage?.id || '',
      user_ids: [],
      due_date: addDays(new Date(), 1),
      type: 'federal_contract_pursuit',
      tag_ids: [],
      estimated_value: null,
      pwin: 0,
      has_unrestricted_access: true,
      values: schema ? buildDefaultValuesFromPursuit(schema, undefined) : {},
    }),
    [schema, minStage],
  )

  const uploadFilesForPursuit = useCallback(
    async (pursuitId: UUID, isStale: () => boolean) => {
      let hasRetryableLocalFiles = false
      let hasAnyLocalFileFailures = false

      // Callers only run with a real org; the guard keeps orgId narrowed for
      // the SharePoint import payload below.
      if (!orgId) {
        return { hasAnyLocalFileFailures, hasRetryableLocalFiles }
      }

      if (selectedFiles.length > 0) {
        const result = await uploadPursuitFiles({
          pursuitId,
          files: selectedFiles,
        })
        hasAnyLocalFileFailures = result.failed.length > 0
        const retryableFiles = result.failed
          .filter((failedUpload) => !failedUpload.fileCreated)
          .map((failedUpload) => failedUpload.file)
        if (!isStale()) {
          setSelectedFiles(retryableFiles)
        }
        hasRetryableLocalFiles = retryableFiles.length > 0
      }

      if (selectedSharepointFiles.length > 0) {
        await importSharepointFiles({
          organization_id: orgId,
          files: selectedSharepointFiles.map((file) => ({
            drive_id: file.drive_id,
            drive_item_id: file.drive_item_id,
            ...(file.tag_ids.length > 0 ? { tag_ids: file.tag_ids } : {}),
            type: 'pursuit_file',
            pursuit_id: pursuitId,
          })),
        })
        if (!isStale()) {
          setSelectedSharepointFiles([])
        }
      }

      return {
        hasAnyLocalFileFailures,
        hasRetryableLocalFiles,
      }
    },
    [
      selectedFiles,
      selectedSharepointFiles,
      orgId,
      uploadPursuitFiles,
      importSharepointFiles,
    ],
  )

  // Retry path for a pursuit that was created but whose file upload failed.
  // Invoked directly by the footer button in retry mode (bypassing form
  // validation — the fields were already submitted with the create) and by
  // handleSubmit as a fallback.
  const retryPendingUploads = useCallback(async () => {
    // Snapshot: Skip/Cancel can null the ref while the upload below is in
    // flight, and re-reading it after the await would hand onSuccess null.
    const pendingPursuit = savedPursuitRef.current
    if (pendingPursuit === null) {
      return
    }
    // Chips stay deletable in retry mode; if the user removed every failed
    // file, "Retry" is really "continue to the pursuit" — don't claim files
    // were uploaded.
    const hadFilesToUpload =
      selectedFiles.length > 0 || selectedSharepointFiles.length > 0
    const generation = submitGenerationRef.current
    const isStale = () => submitGenerationRef.current !== generation
    try {
      const { hasAnyLocalFileFailures, hasRetryableLocalFiles } =
        await uploadFilesForPursuit(pendingPursuit.id, isStale)
      if (isStale() || hasRetryableLocalFiles) {
        return
      }
      if (hadFilesToUpload) {
        // Mirror the first-pass submit: closing silently after a
        // non-retryable failure would read as "everything uploaded".
        if (hasAnyLocalFileFailures) {
          toast.warning(
            'Pursuit created, but some files could not be uploaded.',
          )
        } else {
          toast.success('Pursuit files uploaded successfully!')
        }
      }
      onSuccess?.(pendingPursuit)
      markPursuitSaved(null)
      onClose()
    } catch (error) {
      // No toast here: the upload mutations already surface errors through
      // useHandleError, and a second toast would stack on top of it. The
      // dialog stays in retry mode for another attempt.
      console.error(error)
    }
  }, [
    uploadFilesForPursuit,
    onSuccess,
    onClose,
    markPursuitSaved,
    selectedFiles.length,
    selectedSharepointFiles.length,
  ])

  const handleSubmit = useCallback(
    async ({ value }: { value: CreatePursuitFormValues }) => {
      if (!orgId || !user?.userId) {
        return
      }

      // If we have a saved pursuit (from a previous failed upload), retry the upload
      if (savedPursuitRef.current !== null) {
        await retryPendingUploads()
        return
      }

      const generation = submitGenerationRef.current
      const isStale = () => submitGenerationRef.current !== generation

      // Create the pursuit first
      try {
        const userIds = includeCurrentUserForInviteOnly(
          value.user_ids,
          user.userId,
          value.has_unrestricted_access,
        )
        const createData: CreatePursuit = {
          organization_id: orgId,
          title: value.title,
          description: value.description || null,
          stage_id: value.stage_id as UUID,
          user_ids: userIds,
          due_date: value.due_date,
          type: value.type,
          tag_ids: value.tag_ids,
          estimated_value: value.estimated_value,
          pwin: value.pwin,
          pwin_override: null,
          has_unrestricted_access: value.has_unrestricted_access,
          extended_schema_id: schema?.id ?? null,
          extended_data: value.values,
          [value.type]: {},
        }
        const newPursuit = await createPursuit(createData)
        if (isStale()) {
          // Dialog closed mid-create. The pursuit exists (and is already in
          // the list cache via useCreatePursuit) — just don't navigate the
          // user who cancelled or arm the retry state.
          return
        }

        // If there are files, try to upload them
        if (selectedFiles.length > 0 || selectedSharepointFiles.length > 0) {
          try {
            const { hasAnyLocalFileFailures, hasRetryableLocalFiles } =
              await uploadFilesForPursuit(newPursuit.id, isStale)
            if (isStale()) {
              return
            }
            if (hasRetryableLocalFiles) {
              markPursuitSaved(newPursuit)
              return
            }
            if (hasAnyLocalFileFailures) {
              // Non-retryable failures: the pursuit is fine but some files
              // didn't make it — don't claim full success.
              toast.warning(
                'Pursuit created, but some files could not be uploaded.',
              )
            } else {
              toast.success('Pursuit created successfully!')
            }
          } catch (error) {
            if (isStale()) {
              return
            }
            // If upload fails, save the pursuit and allow retry
            markPursuitSaved(newPursuit)
            console.error(error)
            return
          }
        } else {
          toast.success('Pursuit created successfully!')
        }

        onSuccess?.(newPursuit)
        onClose()
        markPursuitSaved(null)
        setSelectedFiles([])
        setSelectedSharepointFiles([])
      } catch (e) {
        console.error(e)
      }
    },
    [
      selectedFiles,
      selectedSharepointFiles,
      orgId,
      schema,
      user?.userId,
      createPursuit,
      onSuccess,
      onClose,
      uploadFilesForPursuit,
      retryPendingUploads,
    ],
  )

  const dynamicZod = useMemo(
    () => buildZodObjectFromExtendedSchema(schema),
    [schema],
  )

  const form = useAppForm({
    defaultValues,
    validationLogic: revalidateLogic({
      mode: 'submit',
      modeAfterSubmission: 'change',
    }),
    // @ts-expect-error - dynamicZod is a zod object
    validators: { onDynamic: dynamicZod },
    onSubmit: handleSubmit,
  })

  const numErrors = useStore(form.store, (s) => s.errors.length)

  const handleAddFiles = useCallback((files: File[]) => {
    setSelectedFiles((prev) => [...prev, ...files])
  }, [])

  const handleOpenSharepointUpload = useCallback(() => {
    handleSelectSharepoint()
  }, [handleSelectSharepoint])

  const handleCloseSharepointPicker = useCallback(() => {
    setIsSharepointPickerOpen(false)
  }, [])

  const handleSharepointFilesSelected = useCallback(
    async (selection: SharepointFilePickerSelection) => {
      const nextFiles = selection.items
        .filter((item) => !item.is_folder)
        .map((item) => ({
          ...item,
          tag_ids: selection.tag_ids,
        }))

      setSelectedSharepointFiles((prev) =>
        mergeSharepointFiles(prev, nextFiles),
      )
      return true
    },
    [],
  )

  const handleRemoveFile = useCallback(
    (index: number) => () => {
      setSelectedFiles((prev) =>
        prev.filter((_file: File, i: number) => i !== index),
      )
    },
    [],
  )

  const handleRemoveSharepointFile = useCallback(
    (index: number) => () => {
      setSelectedSharepointFiles((prev) =>
        prev.filter((_file, i) => i !== index),
      )
    },
    [],
  )

  const handleFieldSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFieldSearchQuery(e.target.value)
    },
    [],
  )

  // Full transient-state reset for dialog close: retry state, staged files,
  // field search, and the SharePoint picker. Bumping the generation makes any
  // in-flight submit abandon its post-await side effects.
  const resetForClose = useCallback(() => {
    submitGenerationRef.current += 1
    markPursuitSaved(null)
    setSelectedFiles([])
    setSelectedSharepointFiles([])
    setFieldSearchQuery('')
    setIsSharepointPickerOpen(false)
  }, [markPursuitSaved])

  return {
    form,
    schema,
    minStage,
    numErrors,
    isCreatingPursuit,
    isUploadingFiles,
    savedPursuit,
    selectedFiles,
    selectedSharepointFiles,
    isSharepointPickerOpen,
    fieldSearchQuery,
    handleAddFiles,
    handleOpenSharepointUpload,
    handleCloseSharepointPicker,
    handleSharepointFilesSelected,
    handleRemoveFile,
    handleRemoveSharepointFile,
    handleFieldSearchChange,
    resetForClose,
    retryPendingUploads,
    showSharepoint,
    sharepointDisabled,
    sharepointRequiresOrgSetup,
    sharepointSublabel,
    sharepointSublabelTone,
  }
}

export const CreatePursuitDialog: React.FC<CreatePursuitDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const [wizardStep, setWizardStep] = useState<WizardStep>('visibility')

  const handleClose = useCallback(() => {
    setWizardStep('visibility')
    onOpenChange(false)
  }, [onOpenChange])

  const {
    form,
    schema,
    numErrors,
    isCreatingPursuit,
    isUploadingFiles,
    savedPursuit,
    selectedFiles,
    selectedSharepointFiles,
    handleAddFiles,
    handleOpenSharepointUpload,
    handleCloseSharepointPicker,
    handleSharepointFilesSelected,
    handleRemoveFile,
    handleRemoveSharepointFile,
    isSharepointPickerOpen,
    fieldSearchQuery,
    handleFieldSearchChange,
    resetForClose,
    retryPendingUploads,
    showSharepoint,
    sharepointDisabled,
    sharepointRequiresOrgSetup,
    sharepointSublabel,
    sharepointSublabelTone,
  } = useCreatePursuitForm({
    orgId: activeOrgId,
    onClose: handleClose,
    onSuccess: onSuccess,
  })
  const formView = form as unknown as AnyTanstackAppForm

  const isSubmitInFlight = isCreatingPursuit || isUploadingFiles

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      // Block Escape/overlay/X dismissal while a create or upload is running —
      // the dialog closing mid-flight is exactly the state the generation
      // guard has to paper over, so don't offer it in the first place.
      if (!nextOpen && isSubmitInFlight) {
        return
      }
      if (!nextOpen) {
        setWizardStep('visibility')
      }
      onOpenChange(nextOpen)
    },
    [onOpenChange, isSubmitInFlight],
  )

  const hasUnrestrictedAccess = useStore(
    form.store,
    (s) => s.values.has_unrestricted_access,
  )
  const selectedMemberIds = useStore(form.store, (s) => s.values.user_ids)
  const wizardSteps = hasUnrestrictedAccess
    ? PUBLIC_WIZARD_STEPS
    : PRIVATE_WIZARD_STEPS
  const wizardStepIndex = Math.max(
    wizardSteps.findIndex((step) => step.id === wizardStep),
    0,
  )

  // The dialog stays mounted across opens, so the form instance survives a
  // close. Reset it (and the step) whenever the dialog closes — otherwise a
  // previous visibility/member selection silently applies to the next pursuit.
  // resetForClose also clears the failed-upload retry state (a fresh wizard
  // whose submit still targets the previous pursuit's retry-upload path would
  // attach the new files to the old pursuit) and invalidates any in-flight
  // submit's post-await side effects.
  useEffect(() => {
    if (!open) {
      formView.reset()
      setWizardStep('visibility')
      resetForClose()
    }
  }, [open, formView, resetForClose])

  const handleVisibilitySelect = useCallback(
    (id: string) => {
      // Deliberately no user_ids reset on a visibility switch: the details
      // step's Owners field renders user_ids in BOTH flows, so member picks
      // never carry over invisibly — and wiping here destroys Owner picks
      // made on the details step before backtracking to compare options.
      formView.setFieldValue('has_unrestricted_access', id === 'public')
    },
    [formView],
  )

  const handleMembersChange = useCallback(
    (userIds: string[]) => {
      formView.setFieldValue('user_ids', userIds)
    },
    [formView],
  )

  const handleContinueFromVisibility = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // advancing the step re-renders this button into the next step's button
      // before the click's default action runs, so cancel it explicitly
      event.preventDefault()
      setWizardStep(
        form.state.values.has_unrestricted_access ? 'details' : 'members',
      )
    },
    [form],
  )

  const handleContinueToDetails = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      // same mid-click morph hazard as above
      event.preventDefault()
      setWizardStep('details')
    },
    [],
  )

  const handleBackToVisibility = useCallback(() => {
    setWizardStep('visibility')
  }, [])

  const handleBackFromDetails = useCallback(() => {
    setWizardStep(
      form.state.values.has_unrestricted_access ? 'visibility' : 'members',
    )
  }, [form])

  const handleStepSelect = useCallback(
    (index: number) => {
      // Once the pursuit exists (file-upload retry state) the wizard can't
      // rewind — mirrors the hidden Back button on the details step. Also
      // hold the step still while a submit is running.
      if (savedPursuit !== null || isSubmitInFlight) {
        return
      }
      const target = wizardSteps[index]
      if (target) {
        setWizardStep(target.id)
      }
    },
    [wizardSteps, savedPursuit, isSubmitInFlight],
  )

  // The close-reset effect clears the retry/file/search state, so Cancel is
  // just a close.
  const handleCancel = handleClose

  const handleSkipFileUpload = useCallback(() => {
    if (savedPursuit === null) {
      return
    }
    onSuccess?.(savedPursuit)
    handleClose()
  }, [savedPursuit, onSuccess, handleClose])

  const handleRetryClick = useCallback(() => {
    void retryPendingUploads()
  }, [retryPendingUploads])
  const sharepointSettingsHref = activeOrgId
    ? `/orgs/${activeOrgId}/profile?tab=integrations`
    : ''
  const handleOpenSharepointSettings = useCallback(() => {
    if (!sharepointSettingsHref) {
      return
    }

    window.open(sharepointSettingsHref, '_blank', 'noopener,noreferrer')
  }, [sharepointSettingsHref])

  const sharepointDescription =
    sharepointSublabel ??
    'Selected SharePoint files will be imported after the pursuit is created.'
  const sharepointButtonLabel =
    !sharepointDisabled && sharepointSublabelTone === 'warning'
      ? 'Authenticate'
      : 'Choose files'

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="sm:min-w-[700px] max-w-[700px] p-0 h-[min(85vh,640px)] flex flex-col"
      >
        <SharepointFilePicker
          confirmLabel="Add selected"
          isOpen={isSharepointPickerOpen}
          onClose={handleCloseSharepointPicker}
          onSubmit={handleSharepointFilesSelected}
          tagType="pursuit"
        />
        <div className="shrink-0 border-b border-primary-100 px-6 pb-4 pt-5 dark:border-primary-800">
          <DialogHeader>
            <DialogTitle>New Pursuit</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <WizardStepper
              steps={wizardSteps}
              currentIndex={wizardStepIndex}
              ariaLabel="Pursuit creation progress"
              // In retry mode the wizard can't rewind; omitting the handler
              // (rather than swallowing clicks) removes the hover/cursor
              // affordance too.
              onStepSelect={
                savedPursuit !== null ? undefined : handleStepSelect
              }
            />
          </div>
        </div>
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-6 py-5">
          <div className="mb-5 shrink-0 space-y-1">
            <h3 className="text-base font-semibold text-balance text-primary-900 dark:text-primary-100">
              {wizardSteps[wizardStepIndex].heading}
            </h3>
            <p className="text-sm text-pretty text-primary-600 dark:text-primary-400">
              {wizardSteps[wizardStepIndex].description}
            </p>
          </div>
          <form.AppForm>
            {wizardStep === 'visibility' ? (
              <div className="flex flex-col gap-5">
                <OptionsStep
                  ariaLabel="Pursuit visibility"
                  options={VISIBILITY_OPTIONS}
                  selectedId={hasUnrestrictedAccess ? 'public' : 'private'}
                  onSelect={handleVisibilitySelect}
                  orientation="horizontal"
                />
                <WhoGetsAccess
                  orgId={activeOrgId}
                  visibility={hasUnrestrictedAccess ? 'public' : 'private'}
                  selectedUserIds={selectedMemberIds}
                />
              </div>
            ) : null}
            {wizardStep === 'members' ? (
              <MembersStep
                orgId={activeOrgId}
                selectedUserIds={selectedMemberIds}
                onChange={handleMembersChange}
              />
            ) : null}
            {wizardStep === 'details' ? (
              <div className="flex shrink-0 flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <Label>Add Solicitation Files</Label>
                  {/* Locked while submitting: the upload snapshots the file
                      arrays and rewrites them after the await, so files added
                      or removed mid-flight would be silently clobbered. */}
                  <FileUpload
                    onAddFiles={handleAddFiles}
                    disabled={isSubmitInFlight}
                    className="p-6 dark:bg-primary-900"
                  />
                  {showSharepoint && (
                    <div className="rounded-lg border border-primary-200 dark:border-primary-800 bg-primary-50/50 dark:bg-primary-900/30 px-4 py-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div className="flex min-w-0 items-start gap-3">
                          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-primary-200 dark:border-primary-700 bg-white dark:bg-primary-900 shadow-sm">
                            <SharePointIcon size={18} />
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-primary-900 dark:text-primary-100">
                              Import from SharePoint
                            </p>
                            <p
                              className={twMerge(
                                'mt-0.5 text-xs leading-4',
                                sharepointSublabelTone === 'warning'
                                  ? 'text-orange-600 dark:text-orange-400'
                                  : 'text-primary-500 dark:text-primary-400',
                              )}
                            >
                              {sharepointDescription}
                            </p>
                          </div>
                        </div>
                        {sharepointRequiresOrgSetup ? (
                          <Button
                            type="button"
                            variant="outline-compact"
                            onClick={handleOpenSharepointSettings}
                            className="shrink-0 gap-1.5"
                          >
                            <HiOutlineArrowTopRightOnSquare size={14} />
                            <span>Go to settings</span>
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="outline-compact"
                            onClick={handleOpenSharepointUpload}
                            disabled={sharepointDisabled || isSubmitInFlight}
                            className="shrink-0"
                          >
                            <SharePointIcon size={16} />
                            <span>{sharepointButtonLabel}</span>
                          </Button>
                        )}
                      </div>
                    </div>
                  )}
                  {(selectedFiles.length > 0 ||
                    selectedSharepointFiles.length > 0) && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedFiles.map((file: File, index: number) => (
                        <CompactLocalFile
                          key={`${file.name}-${index}`}
                          file={file}
                          onDelete={
                            isSubmitInFlight
                              ? undefined
                              : handleRemoveFile(index)
                          }
                        />
                      ))}
                      {selectedSharepointFiles.map(
                        (file: PendingSharepointFile, index: number) => (
                          <CompactSharepointFile
                            key={`${file.drive_id}-${file.drive_item_id}`}
                            file={file}
                            onDelete={
                              isSubmitInFlight
                                ? undefined
                                : handleRemoveSharepointFile(index)
                            }
                          />
                        ),
                      )}
                    </div>
                  )}
                </div>

                <OverviewSection form={formView} includeType />

                {schema && schema.fields.length > 0 && (
                  <>
                    <Separator />
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center justify-between w-full gap-2 -mt-2">
                        <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
                          Custom Fields ({schema.fields.length})
                        </span>
                        <SearchInput
                          placeholder="Search fields..."
                          value={fieldSearchQuery}
                          onChange={handleFieldSearchChange}
                          className="w-48"
                        />
                      </div>
                      <div className="pt-2">
                        <DynamicFieldList
                          snapshot={schema}
                          form={formView}
                          searchQuery={fieldSearchQuery}
                        />
                      </div>
                    </div>
                  </>
                )}
              </div>
            ) : null}
          </form.AppForm>
        </div>

        <div className="flex shrink-0 items-center justify-between gap-2 w-full px-6 py-4 border-t border-primary-100 dark:border-primary-800">
          <div className="flex-1">
            {savedPursuit !== null ? (
              // Retry mode: the pursuit already exists, so submits only upload
              // the remaining files — edits to the fields above won't save.
              <span className="text-sm text-primary-500 dark:text-primary-400">
                Pursuit created — Retry uploads the remaining files. Field edits
                here won&apos;t be saved.
              </span>
            ) : wizardStep === 'details' && numErrors > 0 ? (
              <ErrorMessage className="self-center">
                Please review and correct the fields with errors.
              </ErrorMessage>
            ) : (
              <span className="text-sm text-primary-500 dark:text-primary-400">
                {`Step ${wizardStepIndex + 1} of ${wizardSteps.length}`}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            {wizardStep === 'visibility' ? (
              <>
                <Button
                  type="button"
                  variant="outline-compact"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary-compact"
                  type="button"
                  onClick={handleContinueFromVisibility}
                >
                  <span>Continue</span>
                  <HiMiniChevronRight className="flex-shrink-0 w-4 h-4" />
                </Button>
              </>
            ) : wizardStep === 'members' ? (
              <>
                <Button
                  variant="outline-compact"
                  type="button"
                  onClick={handleBackToVisibility}
                >
                  <HiMiniChevronLeft className="flex-shrink-0 w-4 h-4" />
                  <span>Back</span>
                </Button>
                <Button
                  variant="primary-compact"
                  type="button"
                  onClick={handleContinueToDetails}
                >
                  <span>Continue</span>
                  <HiMiniChevronRight className="flex-shrink-0 w-4 h-4" />
                </Button>
              </>
            ) : (
              <>
                {savedPursuit === null && (
                  <Button
                    variant="outline-compact"
                    type="button"
                    onClick={handleBackFromDetails}
                    disabled={isSubmitInFlight}
                  >
                    <HiMiniChevronLeft className="flex-shrink-0 w-4 h-4" />
                    <span>Back</span>
                  </Button>
                )}
                <Button
                  type="button"
                  variant="outline-compact"
                  onClick={handleCancel}
                  disabled={isSubmitInFlight}
                >
                  Cancel
                </Button>
                {savedPursuit !== null && (
                  <Button
                    variant="outline-compact"
                    type="button"
                    onClick={handleSkipFileUpload}
                    disabled={isSubmitInFlight}
                  >
                    Skip File Upload and Go to Pursuit
                  </Button>
                )}
                <Button
                  variant="primary-compact"
                  // Stays clickable when the form is invalid: submitting runs
                  // validation, which pink-highlights the offending fields
                  // (aria-invalid) — same pattern as EditPursuitDialog.
                  // Retry mode goes straight to the upload: the fields were
                  // already persisted by the create, so validation would only
                  // block a pure file retry.
                  disabled={isSubmitInFlight}
                  className="flex items-center gap-2"
                  onClick={
                    savedPursuit !== null ? handleRetryClick : form.handleSubmit
                  }
                >
                  {isCreatingPursuit && (
                    <>
                      <Spinner className="w-4 h-4" variant="primary-compact" />
                      Creating pursuit...
                    </>
                  )}
                  {isUploadingFiles && (
                    <>
                      <Spinner className="w-4 h-4" variant="primary-compact" />
                      Uploading files...
                    </>
                  )}
                  {!(isCreatingPursuit || isUploadingFiles) &&
                    (savedPursuit !== null
                      ? 'Retry File Upload'
                      : 'Create Pursuit')}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
